import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import { getRouteUrl } from '@utils/urls';
import { isChicoreeApp } from '@utils/environmentCheck';

import CMSComponentsRenderer from '@common/components/CMSComponents/CMSComponentsRenderer';
import HTMLRenderer from '@common/components/CMSComponents/HTMLRenderer';
import Layout from '@common/containers/Layout';
import RouteLayout from '@common/containers/RouteLayout';
import { HOME, CMS_PAGE } from '@common/routes';
import PageHeroMedia from '@common/components/PageHeroMedia';
import { PriceText } from '@common/components/PriceText';
import DeepLinks from '@common/components/DeepLinks';
import { videoShape } from '@common/types/cms';

import {
  Content,
  ContentContainer,
  Header,
  Lead,
  Title,
  Subtitle,
} from './CMSPage.styled';

export function CMSPage({
  pageData: {
    image,
    lead,
    title,
    body,
    meta,
    video,
    videoDesktop,
    categoryTitle,
  },
}) {
  const {
    query: { slug },
    locale,
  } = useRouter();
  const pageUrl = getRouteUrl(locale, CMS_PAGE, { slug: slug.join('/') });
  const isAppEnvironemnt = isChicoreeApp();

  return (
    <>
      <DeepLinks />

      <Layout
        ogImage={image?.url}
        title={meta.metaTitle}
        description={meta.metaDescription}
        canonicalPageUrl={pageUrl}
        noNavbar={isAppEnvironemnt}
        noFooter={isAppEnvironemnt}
      >
        <RouteLayout
          backRoute={HOME}
          backRouteAs={getRouteUrl(locale, HOME)}
          columns={image ? 12 : 8}
          stickyTitle={title}
          isHero={!!image}
          withoutStickyBar={!title || isAppEnvironemnt}
        >
          <PageHeroMedia
            image={{ src: image?.url, alt: image?.altText || '' }}
            asHeroImage
            video={video}
            videoDesktop={videoDesktop}
          />

          <ContentContainer>
            <Content hasImage={!!image}>
              <Header hasImage={!!image}>
                <Title>
                  {categoryTitle && <Subtitle>{categoryTitle}</Subtitle>}
                  <PriceText>{title}</PriceText>
                </Title>

                {lead && (
                  <Lead>
                    <HTMLRenderer
                      customAs="span"
                      html={lead}
                      nl2br
                    />
                  </Lead>
                )}
              </Header>

              <CMSComponentsRenderer body={body} />
            </Content>
          </ContentContainer>
        </RouteLayout>
      </Layout>
    </>
  );
}

CMSPage.propTypes = {
  pageData: PropTypes.shape({
    body: PropTypes.arrayOf(PropTypes.shape({})),
    image: PropTypes.shape({
      altText: PropTypes.string,
      url: PropTypes.string,
    }),
    video: videoShape,
    videoDesktop: videoShape,
    lead: PropTypes.string,
    meta: PropTypes.shape({
      type: PropTypes.string,
      metaDescription: PropTypes.string,
      metaTitle: PropTypes.string.isRequired,
    }).isRequired,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
    categoryTitle: PropTypes.string,
  }).isRequired,
};

export default CMSPage;
